/**
 * La fiche d'un enfant, consultée par l'équipe : ce qu'il faut savoir en
 * tête, puis le parent, le départ, la santé, la journée, les autorisations,
 * l'identité et les inscriptions — dans la forme des fiches des présences,
 * avec les questions du formulaire des parents.
 *
 * Relue à chaque ouverture (et d'un clic sur « Recharger ») : une
 * modification, par l'équipe ou par le parent, se voit aussitôt.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AlertCircle, AlertTriangle, CalendarDays, Camera, ChevronLeft, ClipboardList, HeartPulse, Loader2, Mail, Pencil, Phone,
  RefreshCw, Users, Waves,
} from 'lucide-react'
import { anneeScolaireCourante } from '../../../../lib/classes/regles'
import { libelleAnneeScolaire } from '../../../../lib/jpa/regles'
import { lireNRN, chiffresNRN } from '../../../../lib/registreNational'
import { DESCRIPTIONS } from '../../../../lib/droitImage'
import { ageEnClair, dateEnClair } from '../../../../lib/presences/lien'
import { PHRASE_NATATION, ecoleEnregistree } from '../../../../components/forms/kid/fiche'
import { lireFicheEquipe, type LectureFiche } from '../ficheEnfant/lecture'
import { NiveauDAcces, type NiveauDAccesLu } from '../../NiveauDAcces'
import { anciennesReponses } from '../ficheEnfant/regles'
import { aSavoir } from './regles'
import { lireLesInscriptions, signerLesPhotos, type InscriptionDeLEnfant } from './lecture'
import { Avatar, LigneASavoirVue } from './Lignes'

const aujourdHui = () => new Date().toISOString().slice(0, 10)
const ouiNon = (v: boolean) => (v ? 'Oui' : 'Non')
const ACCOMPAGNEMENT: Record<string, string> = { yes: 'Oui, en permanence', sometimes: 'Par moments', no: 'Non' }

export function FicheConsultee({
  kidId,
  ecoles,
  onRetour,
  onModifier,
}: {
  kidId: string
  /** Le nom des écoles de la liste, par identifiant. */
  ecoles: Map<string, string>
  onRetour: () => void
  onModifier?: () => void
}) {
  const annee = useMemo(() => anneeScolaireCourante(), [])
  const [lecture, setLecture] = useState<LectureFiche | null>(null)
  const [inscriptions, setInscriptions] = useState<InscriptionDeLEnfant[]>([])
  const [photo, setPhoto] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    try {
      const [l, i] = await Promise.all([lireFicheEquipe(kidId, annee), lireLesInscriptions(kidId)])
      setLecture(l)
      setInscriptions(i)
      const chemin = (l.lignes.kids?.photo_url ?? '').trim()
      setPhoto(chemin ? (await signerLesPhotos([chemin]).catch(() => new Map<string, string>())).get(chemin) ?? null : null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setChargement(false)
    }
  }, [kidId, annee])

  useEffect(() => {
    void charger()
  }, [charger])

  const f = lecture?.fiche
  const prenom = f?.identite.prenom.trim() || 'L’enfant'
  const lignes = f ? aSavoir(f, (lecture?.personnes ?? []).map(p => ({ id: p.id, first_name: p.first_name, last_name: p.last_name }))) : []
  const ecoleId = f ? ecoleEnregistree(f.identite) : ''
  const ecole = ecoles.get(ecoleId) ?? (ecoleId || null)
  const age = f ? ageEnClair(f.identite.dateNaissance || null, aujourdHui()) : null
  const anciennes = lecture ? anciennesReponses(lecture.lignes) : null
  const niveau = (lecture?.lignes.inclusion ?? {}) as NiveauDAccesLu

  return (
    <div className="overflow-hidden rounded-2xl bg-gray-50 ring-1 ring-gray-200">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-200 bg-white/95 px-2 py-2 backdrop-blur">
        <button
          type="button"
          onClick={onRetour}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
          aria-label="Revenir à la liste"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        {f && <Avatar photo={photo} prenom={f.identite.prenom} nom={f.identite.nom} taille="sm" />}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-gray-900">
            {f ? `${f.identite.prenom} ${f.identite.nom}` : 'Fiche'}
          </h2>
          {f && (
            <p className="truncate text-sm text-gray-600">
              {[age, ecole, lecture?.classe?.libelle].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void charger()}
          disabled={chargement}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          aria-label="Recharger la fiche"
          title="Recharger la fiche"
        >
          <RefreshCw className={`h-5 w-5 ${chargement ? 'animate-spin' : ''}`} />
        </button>
        {onModifier && f && (
          <button
            type="button"
            onClick={onModifier}
            aria-label="Modifier la fiche"
            className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Modifier</span>
          </button>
        )}
      </div>

      <main className="space-y-4 p-4">
        {erreur && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>La fiche n’a pas pu être lue : {erreur}</span>
          </div>
        )}
        {chargement && !lecture && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {f && lecture && (
          <>
            <p className="text-xs text-gray-500">
              {lecture.lignes.kids?.last_verified_at
                ? `Fiche vérifiée par le parent le ${dateEnClair(lecture.lignes.kids.last_verified_at.slice(0, 10))}.`
                : 'Fiche pas encore vérifiée par le parent avec le formulaire actuel.'}
            </p>

            {lignes.length > 0 && (
              <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4" /> À savoir
                </h3>
                <dl className="mt-2 space-y-1.5">
                  {lignes.map(l => <LigneASavoirVue key={l.cle} ligne={l} />)}
                </dl>
              </section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <Section titre="Parent" icone={<Phone className="h-4 w-4" />}>
                  <Ligne
                    libelle={lecture.parent?.account_type === 'organisation' ? 'Organisation' : 'Nom'}
                    valeur={`${lecture.parent?.prenom ?? ''} ${lecture.parent?.nom ?? ''}`.trim() || null}
                  />
                  <Telephone libelle="Téléphone" numero={lecture.parent?.telephone ?? null} />
                  <Telephone libelle="Autre téléphone" numero={lecture.parent?.telephone2 ?? null} />
                  {lecture.parent?.email ? (
                    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                      <span className="text-gray-500">E-mail</span>
                      <a href={`mailto:${lecture.parent.email}`} className="inline-flex items-center gap-1.5 truncate font-medium text-amber-800">
                        <Mail className="h-4 w-4 flex-shrink-0" /> <span className="truncate">{lecture.parent.email}</span>
                      </a>
                    </div>
                  ) : (
                    <Ligne libelle="E-mail" valeur={null} />
                  )}
                </Section>

                <Section titre="Départ" icone={<ClipboardList className="h-4 w-4" />}>
                  <Ligne libelle="Rentre seul·e" valeur={ouiNon(f.sorties.partSeul)} alerte={f.sorties.partSeul} />
                  {f.sorties.partSeul ? (
                    <Ligne libelle="À partir de" valeur={f.sorties.heureDepart || null} alerte />
                  ) : (
                    <div className="pt-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Personnes autorisées</p>
                      {f.sorties.personnesIds.length === 0 ? (
                        <p className="py-1 text-sm text-amber-800">Aucune personne autorisée.</p>
                      ) : (
                        <ul className="mt-1 space-y-1">
                          {lecture.personnes.filter(p => f.sorties.personnesIds.includes(p.id)).map(p => (
                            <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                              <span className="text-gray-900">
                                {p.first_name} {p.last_name}
                                {p.relationship ? <span className="text-gray-500"> · {p.relationship}</span> : null}
                              </span>
                              {p.phone_number && (
                                <a href={`tel:${p.phone_number.replace(/\s/g, '')}`} className="font-medium text-amber-800">
                                  {p.phone_number}
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </Section>

                <Section titre="Santé et alimentation" icone={<HeartPulse className="h-4 w-4" />}>
                  <Ligne libelle="Allergies" valeur={f.sante.allergies ? `Oui — ${f.sante.allergiesDetails || '?'}` : 'Non'} alerte={f.sante.allergies} />
                  {f.sante.allergies && (
                    <Ligne libelle="En cas de contact" valeur={f.sante.allergiesConsequences || null} alerte />
                  )}
                  <Ligne libelle="Régime" valeur={f.sante.regimeSpecial ? `Oui — ${f.sante.regimeDetails || '?'}` : 'Non'} alerte={f.sante.regimeSpecial} />
                  <Ligne libelle="Médicaments" valeur={f.sante.medication ? `Oui — ${f.sante.medicationDetails || '?'}` : 'Non'} alerte={f.sante.medication} />
                  {f.sante.medication && (
                    <>
                      <Ligne libelle="Les prend seul·e" valeur={ouiNon(f.sante.medicationAutonomie)} />
                      <Ligne libelle="Formulaire de médication" valeur={lecture.reperes.formulaireMedication ? 'Envoyé' : 'Pas encore'} />
                    </>
                  )}
                  <Ligne libelle="Informations médicales" valeur={f.sante.infosMedicales || null} />
                  <Ligne libelle="Antécédents" valeur={f.sante.antecedents || null} />
                  <Ligne libelle="Tétanos à jour" valeur={ouiNon(f.sante.tetanos)} />
                  <Ligne libelle="Médecin" valeur={f.sante.medecinNom || null} />
                  <Telephone libelle="Tél. médecin" numero={f.sante.medecinTel || null} />
                </Section>
              </div>

              <div className="space-y-4">
                <Section titre="Journée en stage" icone={<Waves className="h-4 w-4" />}>
                  <Ligne libelle="Peut tout faire" valeur={ouiNon(f.vie.participeATout)} alerte={!f.vie.participeATout} />
                  {!f.vie.participeATout && <Ligne libelle="Restrictions" valeur={f.vie.restrictions || null} alerte />}
                  <Ligne
                    libelle="Natation"
                    valeur={
                      f.vie.natation
                        ? PHRASE_NATATION[f.vie.natation](prenom)
                        : anciennes?.natation
                          ? `${anciennes.natation.saitNager ? 'Sait nager' : 'Ne sait pas nager'}${anciennes.natation.niveau ? ` (« ${anciennes.natation.niveau} »)` : ''} — ancienne réponse`
                          : null
                    }
                    alerte={f.vie.natation === 'pas du tout' || f.vie.natation === 'difficilement'}
                  />
                  <Ligne libelle="Peur de l’eau" valeur={ouiNon(f.vie.peurDeLEau)} alerte={f.vie.peurDeLEau} />
                  <Ligne libelle="Autres informations" valeur={f.vie.autresInfos || null} />
                </Section>

                {(f.vie.besoinsSpecifiques || niveau.niveau_acces != null) && (
                  <Section titre="Besoins particuliers" icone={<AlertTriangle className="h-4 w-4" />}>
                    {/*
                      Le niveau d'accès (Gestion › Inclusion, 26/10/2026) : il
                      s'enregistre seul, hors du reste de la fiche.
                    */}
                    <div className="pb-2">
                      <NiveauDAcces
                        key={kidId}
                        kidId={kidId}
                        prenom={prenom}
                        decision={niveau}
                        onDecide={decision => setLecture(prev => prev && {
                          ...prev, lignes: { ...prev.lignes, inclusion: { ...prev.lignes.inclusion, ...decision } },
                        })}
                      />
                    </div>
                    {f.vie.besoinsSpecifiques && <>
                    <Ligne libelle="Situation" valeur={f.vie.situation || null} alerte />
                    <Ligne libelle="Dans une journée" valeur={f.vie.impact || null} />
                    <Ligne
                      libelle="Accompagnement dédié"
                      valeur={
                        [f.vie.accompagnementDedie ? ACCOMPAGNEMENT[f.vie.accompagnementDedie] ?? f.vie.accompagnementDedie : null, f.vie.accompagnementDetails || null]
                          .filter(Boolean)
                          .join(' — ') || null
                      }
                    />
                    <Ligne libelle="Ce qui fonctionne" valeur={f.vie.strategies || null} />
                    <Ligne libelle="Matériel, aides" valeur={f.vie.materiel || null} />
                    <Ligne libelle="Signes de stress" valeur={f.vie.signauxDeStress || null} />
                    <Ligne libelle="Ce qui lui réussit" valeur={f.vie.forces || null} />
                    <Ligne libelle="Expériences" valeur={f.vie.experiences || null} />
                    </>}
                  </Section>
                )}

                <Section titre="Autorisations" icone={<Camera className="h-4 w-4" />}>
                  <Ligne libelle="Accord pour les soins" valeur={ouiNon(f.autorisations.soins)} alerte={!f.autorisations.soins} />
                  {f.autorisations.image.espacesPrives === null ? (
                    <p className="py-1.5 text-sm text-amber-800">
                      Droit à l’image : le parent n’a pas encore répondu — <strong>pas de photo</strong>.
                      {anciennes?.photoConsent !== null && anciennes?.photoConsent !== undefined && (
                        <> Ancien accord global : {anciennes.photoConsent ? 'oui' : 'non'}.</>
                      )}
                    </p>
                  ) : (
                    <>
                      {DESCRIPTIONS.map(d => (
                        <Ligne
                          key={d.cle}
                          libelle={`${d.lettre}. ${d.titre}`}
                          valeur={f.autorisations.image[d.cle] === 'oui' ? 'Oui' : 'Non'}
                          alerte={f.autorisations.image[d.cle] !== 'oui'}
                        />
                      ))}
                      <Ligne libelle="Accord de l’autre parent" valeur={ouiNon(f.autorisations.image.accordAutreParent)} />
                      {lecture.lignes.image_rights?.consented_at && (
                        <p className="pt-1.5 text-xs text-gray-500">
                          Réponses du {dateEnClair(lecture.lignes.image_rights.consented_at.slice(0, 10))}, enregistrées par{' '}
                          {lecture.lignes.image_rights.consented_by === lecture.lignes.kids?.user_id ? 'le parent' : 'l’équipe'}.
                        </p>
                      )}
                    </>
                  )}
                </Section>

                <Section titre="Identité et école" icone={<Users className="h-4 w-4" />}>
                  <Ligne
                    libelle="Naissance"
                    valeur={f.identite.dateNaissance ? `${dateEnClair(f.identite.dateNaissance)}${age ? ` (${age})` : ''}` : null}
                  />
                  <Ligne
                    libelle="Registre national"
                    valeur={
                      !chiffresNRN(f.identite.nNational)
                        ? 'Manquant — pas d’attestation fiscale'
                        : lireNRN(f.identite.nNational).ok ? 'Renseigné' : 'Renseigné, à corriger'
                    }
                    alerte={!chiffresNRN(f.identite.nNational) || !lireNRN(f.identite.nNational).ok}
                  />
                  <Ligne
                    libelle="Adresse"
                    valeur={[f.identite.adresse, `${f.identite.cPostal} ${f.identite.localite}`.trim()].filter(Boolean).join(', ') || null}
                  />
                  <Ligne libelle="École" valeur={ecole} />
                  <Ligne
                    libelle={`Classe ${libelleAnneeScolaire(annee)}`}
                    valeur={lecture.classe ? `${lecture.classe.libelle}${lecture.classe.active ? '' : ' (classe fermée)'}` : null}
                  />
                </Section>
              </div>
            </div>

            <Section titre={`Inscriptions (${inscriptions.filter(i => !i.annulee).length})`} icone={<CalendarDays className="h-4 w-4" />}>
              {inscriptions.length === 0 ? (
                <p className="py-1.5 text-sm text-gray-500">Aucune inscription.</p>
              ) : (
                inscriptions.slice(0, 12).map(i => (
                  <div key={i.id} className={`flex flex-wrap items-baseline justify-between gap-x-3 py-1.5 text-sm ${i.annulee ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    <span className="min-w-0">
                      {i.stage}
                      <span className="text-gray-500"> · {i.centre}{i.sousGroupe ? ` · ${i.sousGroupe}` : ''}</span>
                    </span>
                    <span className="text-gray-500">
                      {i.debut ? dateEnClair(i.debut) : ''}{i.fin && i.fin !== i.debut ? ` – ${dateEnClair(i.fin)}` : ''}
                    </span>
                  </div>
                ))
              )}
              {inscriptions.length > 12 && (
                <p className="pt-1.5 text-xs text-gray-500">Et {inscriptions.length - 12} plus anciennes.</p>
              )}
            </Section>
          </>
        )}
      </main>
    </div>
  )
}

function Section({ titre, icone, children }: { titre: string; icone: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <span className="text-gray-400">{icone}</span>
        {titre}
      </h3>
      <div className="mt-2 divide-y divide-gray-100">{children}</div>
    </section>
  )
}

function Ligne({ libelle, valeur, alerte = false }: { libelle: string; valeur: string | null; alerte?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="min-w-0 text-gray-500">{libelle}</span>
      <span className={`max-w-[65%] break-words text-right ${valeur ? (alerte ? 'font-medium text-amber-900' : 'text-gray-900') : 'text-gray-300'}`}>
        {valeur ?? '—'}
      </span>
    </div>
  )
}

function Telephone({ libelle, numero }: { libelle: string; numero: string | null }) {
  if (!numero) return <Ligne libelle={libelle} valeur={null} />
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-gray-500">{libelle}</span>
      <a
        href={`tel:${numero.replace(/\s/g, '')}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 font-medium text-amber-900"
      >
        <Phone className="h-4 w-4" /> {numero}
      </a>
    </div>
  )
}
